import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '../../base/Text';
import { getDisplayImageUrl } from '@/lib/image-url';
import { Button } from '../../base/Button';
import { IconButton } from '../../base/IconButton';
import { Input } from '../../base/Input';
import { TagInput, type TagInputRef } from '../../base/TagInput';
import { Spinner } from '../../base/Spinner';
import { FavoriteToggle } from '../FavoriteToggle';
import type { LinkItemData } from '../LinkItem';
import styles from './LinkDetail.module.css';

export type LinkDetailVariant = 'modal' | 'panel';

interface LinkDetailProps {
  link: LinkItemData;
  onClose: () => void;
  onOpenLink: () => void;
  onDelete: () => void;
  onFavoriteToggle: () => void;
  onUpdate?: (id: string, data: { customTitle?: string | null; tags?: string[] }) => void;
  isDeleting?: boolean;
  isUpdating?: boolean;
  variant?: LinkDetailVariant;
}

const getDomain = (url: string): string => {
  try {
    const domain = new URL(url).hostname;
    return domain.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const LinkDetail = ({
  link,
  onClose,
  onOpenLink,
  onDelete,
  onFavoriteToggle,
  onUpdate,
  isDeleting = false,
  isUpdating = false,
  variant = 'modal',
}: LinkDetailProps) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(link.customTitle ?? '');
  const [editedTags, setEditedTags] = useState(link.tags ?? []);
  const tagInputRef = useRef<TagInputRef>(null);

  // Sincroniza estado local quando a prop link muda (optimistic update)
  useEffect(() => {
    setEditedTitle(link.customTitle ?? '');
    setEditedTags(link.tags ?? []);
  }, [link.customTitle, link.tags]);

  const handleSave = async () => {
    if (onUpdate) {
      // Pega tags atualizadas diretamente do flush (inclui tag pendente se houver)
      const finalTags = tagInputRef.current?.flushPendingTag() || editedTags;
      
      // Chama o update (que faz optimistic update)
      await onUpdate(link.id, {
        customTitle: editedTitle.trim() || null,
        tags: finalTags,
      });
      
      // Sai do modo de edição DEPOIS do optimistic update
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditedTitle(link.customTitle ?? '');
    setEditedTags(link.tags ?? []);
    setIsEditing(false);
  };

  useEffect(() => {
    if (variant !== 'modal') return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [variant, onClose]);

  const overlayClass = variant === 'panel' ? styles.panelOverlay : styles.overlay;
  const containerClass = variant === 'panel' ? styles.panelContainer : styles.container;

  return (
    <div
      className={overlayClass}
      onClick={variant === 'modal' ? onClose : undefined}
      role={variant === 'modal' ? 'dialog' : undefined}
      aria-modal={variant === 'modal' ? true : undefined}
    >
      {/* Loading overlay - aparece durante o salvamento */}
      {isUpdating && (
        <div className={styles.loadingOverlay}>
          <Spinner size="lg" />
          <Text variant="body" className={styles.loadingText}>
            {t('linkDetail.saving')}
          </Text>
        </div>
      )}
      
      <div className={containerClass} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <IconButton
            variant="ghost"
            size="sm"
            label="Close"
            onClick={onClose}
          >
            ←
          </IconButton>
          <div className={styles.headerActions}>
            {!isEditing && onUpdate && (
              <IconButton
                variant="ghost"
                size="sm"
                label={t('linkDetail.edit')}
                onClick={() => setIsEditing(true)}
              >
                ✎
              </IconButton>
            )}
            <FavoriteToggle
              isFavorite={link.isFavorite}
              onToggle={onFavoriteToggle}
            />
          </div>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* Image */}
          {getDisplayImageUrl(link.image) && (
            <div className={styles.imageWrapper}>
              <img
                src={getDisplayImageUrl(link.image)!}
                alt=""
                className={styles.image}
                referrerPolicy="no-referrer"
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  const wrapper = img.parentElement;
                  if (wrapper) wrapper.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Title */}
          {isEditing ? (
            <div className={styles.editSection}>
              <label htmlFor="custom-title" className={styles.label}>
                <Text variant="body">{t('linkDetail.customTitle')}</Text>
              </label>
              <Input
                id="custom-title"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                placeholder={link.title || getDomain(link.url)}
              />
            </div>
          ) : (
            <Text variant="display" className={styles.title}>
              {link.customTitle || link.title || getDomain(link.url)}
            </Text>
          )}

          {/* Tags */}
          {isEditing ? (
            <div className={styles.editSection}>
              <label htmlFor="tags" className={styles.label}>
                <Text variant="body">{t('linkDetail.tags')}</Text>
              </label>
              <TagInput
                ref={tagInputRef}
                tags={editedTags}
                onChange={setEditedTags}
              />
            </div>
          ) : (
            link.tags && link.tags.length > 0 && (
              <div className={styles.tagsDisplay}>
                {link.tags.map((tag) => (
                  <span key={tag} className={styles.tag}>
                    #{tag}
                  </span>
                ))}
              </div>
            )
          )}

          {/* Source & Date */}
          <div className={styles.meta}>
            <Text variant="meta" className={styles.source}>
              {getDomain(link.url)}
            </Text>
            <Text variant="meta">
              Saved {formatDate(link.createdAt)}
            </Text>
          </div>

          {/* Description */}
          {link.description && (
            <Text variant="body" className={styles.description}>
              {link.description}
            </Text>
          )}

          {/* URL */}
          <div className={styles.urlBox}>
            <Text variant="meta" className={styles.url}>
              {link.url}
            </Text>
          </div>
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          {isEditing ? (
            <>
              <Button
                variant="primary"
                fullWidth
                onClick={handleSave}
                disabled={isUpdating}
              >
                {isUpdating ? t('linkDetail.saving') : t('linkDetail.save')}
              </Button>
              <Button
                variant="ghost"
                fullWidth
                onClick={handleCancel}
                disabled={isUpdating}
              >
                {t('linkDetail.cancel')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                fullWidth
                onClick={onOpenLink}
              >
                {t('linkDetail.open')}
              </Button>
              <Button
                variant="ghost"
                fullWidth
                onClick={onDelete}
                disabled={isDeleting}
                className={styles.deleteButton}
              >
                {isDeleting ? t('linkDetail.deleting') : t('linkDetail.delete')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

